import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiFetch } from '../lib/api';
import { tagClass, statusName } from '../lib/tenant';
import { Icons } from '../components/icons';
import { AdminPagedTbody } from '../lib/pagination';

export function TenantDetailPanel({ tenant, onClose, onUpdated }) {
  const [detail, setDetail] = useState(tenant);
  const [plans, setPlans] = useState([]);
  const [showPlan, setShowPlan] = useState(false);
  const [planId, setPlanId] = useState(tenant.plan_id || '');
  const [busy, setBusy] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState(null);
  const [stats, setStats] = useState(null);
  const [aiPolicy, setAiPolicy] = useState(null);
  const [aiPolicyBusy, setAiPolicyBusy] = useState(false);
  const [aiPolicyForm, setAiPolicyForm] = useState({
    allow_byok: false,
    allow_platform_ai: true,
    platform_ai_quota_tokens: 0,
    byok_note: '',
  });
  const [mesInfo, setMesInfo] = useState(null);
  const [mesBusy, setMesBusy] = useState(false);
  const [mesIndustry, setMesIndustry] = useState('electronics');

  const loadAiPolicy = useCallback(() => {
    apiFetch('/api/v1/platform/tenants/' + tenant.id + '/ai-policy').then(d => {
      if (d.code === 0 && d.data) {
        setAiPolicy(d.data);
        setAiPolicyForm({
          allow_byok: !!d.data.allow_byok,
          allow_platform_ai: d.data.allow_platform_ai !== false,
          platform_ai_quota_tokens: d.data.platform_ai_quota_tokens || 0,
          byok_note: d.data.byok_note || '',
        });
      }
    });
  }, [tenant.id]);

  const loadMes = useCallback(() => {
    apiFetch('/api/v1/platform/tenants/' + tenant.id + '/mes').then(d => {
      if (d.code === 0 && d.data) {
        setMesInfo(d.data);
        const ind = d.data.settings?.industry || 'electronics';
        setMesIndustry(ind);
      }
    });
  }, [tenant.id]);

  const loadApiKeys = useCallback(() => {
    apiFetch('/api/v1/platform/tenants/' + tenant.id + '/api-keys').then(d => {
      if (d.code === 0) setApiKeys(d.data || []);
    });
  }, [tenant.id]);

  useEffect(() => {
    apiFetch('/api/v1/platform/tenants/' + tenant.id).then(d => { if (d.code === 0) setDetail(d.data); });
    apiFetch('/api/v1/plans').then(d => { if (d.code === 0) setPlans(d.data || []); });
    apiFetch('/api/v1/platform/tenants/' + tenant.id + '/stats').then(d => { if (d.code === 0) setStats(d.data); });
    loadApiKeys();
    loadAiPolicy();
    loadMes();
  }, [tenant.id, loadApiKeys, loadAiPolicy, loadMes]);

  const toggleStatus = async () => {
    setBusy(true);
    const path = detail.status === 'active'
      ? '/api/v1/platform/tenants/' + detail.id + '/suspend'
      : '/api/v1/platform/tenants/' + detail.id + '/activate';
    const d = await apiFetch(path, { method: 'POST' });
    setBusy(false);
    if (d.code === 0) { setDetail(d.data); onUpdated?.(d.data); }
    else alert(d.message || '操作失败');
  };

  const changePlan = async () => {
    if (!planId) return;
    setBusy(true);
    const d = await apiFetch('/api/v1/platform/tenants/' + detail.id + '/plan', { method: 'PUT', body: { plan_id: planId } });
    setBusy(false);
    if (d.code === 0) { setDetail(d.data); onUpdated?.(d.data); setShowPlan(false); }
    else alert(d.message || '变更失败');
  };

  const resetPassword = async () => {
    if (!confirm('确认重置此租户的超级管理员密码？')) return;
    setBusy(true);
    const d = await apiFetch('/api/v1/platform/tenants/' + detail.id + '/reset-admin-password', { method: 'POST' });
    setBusy(false);
    if (d.code === 0) alert('密码已重置为: ' + d.data.temporary_password);
    else alert(d.message || '重置失败');
  };

  const requestDelete = async () => {
    if (!confirm('确认将租户「' + detail.name + '」标记为待删除？\n进入 30 天冷静期，期间可恢复。')) return;
    setBusy(true);
    const d = await apiFetch('/api/v1/platform/tenants/' + detail.id + '/delete', { method: 'POST' });
    setBusy(false);
    if (d.code === 0) { setDetail(d.data); onUpdated?.(d.data); }
    else alert(d.message || '操作失败');
  };

  const restoreTenant = async () => {
    setBusy(true);
    const d = await apiFetch('/api/v1/platform/tenants/' + detail.id + '/restore', { method: 'POST' });
    setBusy(false);
    if (d.code === 0) { setDetail(d.data); onUpdated?.(d.data); }
    else alert(d.message || '恢复失败');
  };

  const purgeTenant = async () => {
    const force = confirm('物理清除不可恢复！\n\n确定立即清除租户「' + detail.name + '」的所有数据？\n（冷静期未满将强制清除）');
    if (!force) return;
    setBusy(true);
    const d = await apiFetch('/api/v1/platform/tenants/' + detail.id + '/purge', { method: 'POST', body: { force: true } });
    setBusy(false);
    if (d.code === 0) { alert('租户已物理清除'); onUpdated?.({ ...detail, status: 'purged' }); onClose(); }
    else alert(d.message || '清除失败');
  };

  const createApiKey = async () => {
    if (!newKeyName.trim()) return alert('请填写密钥用途名称');
    setBusy(true);
    const d = await apiFetch('/api/v1/platform/tenants/' + detail.id + '/api-keys', {
      method: 'POST', body: { name: newKeyName.trim(), scopes: ['sop:read', 'execution:read', 'mes:write'], expires_in_days: 365 }
    });
    setBusy(false);
    if (d.code === 0) {
      setCreatedKey(d.data);
      setShowKeyModal(false);
      setNewKeyName('');
      loadApiKeys();
    } else alert(d.message || '创建失败');
  };

  const convertTrial = async () => {
    if (!confirm('确认将租户「' + detail.name + '」从试用转为正式版？')) return;
    setBusy(true);
    const d = await apiFetch('/api/v1/platform/tenants/' + detail.id + '/convert-trial', { method: 'POST', body: { plan_code: 'starter' } });
    setBusy(false);
    if (d.code === 0) { setDetail(d.data); onUpdated?.(d.data); }
    else alert(d.message || '转换失败');
  };

  const revokeApiKey = async (k) => {
    if (!confirm('确认吊销密钥「' + k.name + '」？')) return;
    const d = await apiFetch('/api/v1/platform/tenants/' + detail.id + '/api-keys/' + k.id, { method: 'DELETE' });
    if (d.code === 0) loadApiKeys(); else alert(d.message || '吊销失败');
  };

  const saveAiPolicy = async () => {
    setAiPolicyBusy(true);
    const d = await apiFetch('/api/v1/platform/tenants/' + detail.id + '/ai-policy', {
      method: 'PUT',
      body: aiPolicyForm,
    });
    setAiPolicyBusy(false);
    if (d.code === 0) {
      setAiPolicy(d.data);
      loadAiPolicy();
    } else alert(d.message || '保存失败');
  };

  const provisionMes = async (enabled) => {
    if (enabled && mesInfo && mesInfo.plan_allows === false) {
      alert('当前套餐未包含 MES 能力，请先变更套餐（starter/pro/enterprise）');
      return;
    }
    const label = enabled ? '开通' : '停用';
    if (!confirm('确认向 MES 同步「' + label + '」租户「' + detail.name + '」？')) return;
    setMesBusy(true);
    const d = await apiFetch('/api/v1/platform/tenants/' + detail.id + '/mes/provision', {
      method: 'POST',
      body: { industry: mesIndustry, enabled },
    });
    setMesBusy(false);
    if (d.code === 0) {
      loadMes();
      alert(enabled ? 'MES 已开通' : 'MES 已停用');
    } else {
      alert(d.message || label + '失败');
    }
  };

  return <>
    <div className="detail-panel-backdrop" onClick={onClose} />
    <div className="detail-panel">
      <div className="detail-panel-header">
        <div className="detail-panel-title-block">
          <div className="detail-panel-title">{detail.name}</div>
          <div className="detail-panel-sub">
            <code>{detail.code}</code>
            <span className={`tag ${tagClass(detail.status)}`}>{statusName(detail.status)}</span>
          </div>
        </div>
        <button className="btn btn-xs btn-outline" onClick={onClose} aria-label="关闭">×</button>
      </div>
      <div className="detail-panel-body">
        <div className="detail-section">
          <div className="detail-section-title">基本信息</div>
          <div className="detail-row"><span className="detail-label">套餐</span><span className="detail-value">{detail.plan_name || '未分配'}</span></div>
          <div className="detail-row"><span className="detail-label">创建时间</span><span className="detail-value" style={{fontSize:12}}>{new Date(detail.created_at).toLocaleString('zh-CN')}</span></div>
          {detail.trial_ends_at && <div className="detail-row"><span className="detail-label">试用到期</span><span className="detail-value" style={{fontSize:12}}>{new Date(detail.trial_ends_at).toLocaleString('zh-CN')}</span></div>}
          {detail.deleted_at && <div className="detail-row"><span className="detail-label">注销时间</span><span className="detail-value" style={{fontSize:12}}>{new Date(detail.deleted_at).toLocaleString('zh-CN')}</span></div>}
          {detail.purge_eligible_at && detail.status === 'deleted_pending' && <div className="detail-row"><span className="detail-label">可清除时间</span><span className="detail-value" style={{fontSize:12,color:'#dc2626'}}>{new Date(detail.purge_eligible_at).toLocaleString('zh-CN')}</span></div>}
        </div>
        <div className="detail-section">
          <div className="detail-section-title">配额信息</div>
          <div className="detail-row"><span className="detail-label">最大用户数</span><span className="detail-value">{detail.max_users}</span></div>
          <div className="detail-row"><span className="detail-label">最大工位数</span><span className="detail-value">{detail.max_stations}</span></div>
          <div className="detail-row"><span className="detail-label">存储配额</span><span className="detail-value">{detail.max_storage_gb} GB</span></div>
        </div>
        {stats && <div className="detail-section">
          <div className="detail-section-title">实际用量</div>
          <div className="detail-usage-grid">
            <div><span>SOP</span><strong>{stats.sop_count||0}</strong></div>
            <div><span>执行记录</span><strong>{stats.execution_count||0}</strong></div>
            <div><span>用户</span><strong>{stats.user_count||0}</strong></div>
            <div><span>审批流</span><strong>{stats.workflow_count||0}</strong></div>
            <div><span>工厂</span><strong>{stats.plant_count||0}</strong></div>
            <div><span>工位</span><strong>{stats.station_count||0}</strong></div>
          </div>
          {stats.last_activity_at && <div style={{fontSize:11,color:'var(--brand-text-muted)',marginTop:8}}>
            最近活动 {new Date(stats.last_activity_at).toLocaleString('zh-CN')}
          </div>}
        </div>}
        {detail.status !== 'purged' && <div className="detail-section">
          <div className="detail-section-title">AI 权限</div>
          {aiPolicy && <div style={{fontSize:12,color:'var(--brand-text-muted)',marginBottom:10,lineHeight:1.5}}>
            本月平台 AI：{aiPolicy.platform_used_tokens || 0} / {aiPolicy.platform_ai_quota_tokens > 0 ? aiPolicy.platform_ai_quota_tokens : (aiPolicy.plan_default_quota_tokens || '—')} tokens
            {aiPolicy.byok_configured ? ' · BYOK 已配置' : ''}
          </div>}
          <label className="detail-check"><input type="checkbox" checked={aiPolicyForm.allow_platform_ai} onChange={e => setAiPolicyForm(f => ({ ...f, allow_platform_ai: e.target.checked }))} />允许使用平台 AI</label>
          <label className="detail-check"><input type="checkbox" checked={aiPolicyForm.allow_byok} onChange={e => setAiPolicyForm(f => ({ ...f, allow_byok: e.target.checked }))} />允许 BYOK（租户自配 Key）</label>
          <div className="form-group" style={{marginBottom:8}}>
            <label style={{fontSize:12}}>平台 AI 配额覆盖（0=用套餐默认）</label>
            <input className="form-input" type="number" value={aiPolicyForm.platform_ai_quota_tokens}
              onChange={e => setAiPolicyForm(f => ({ ...f, platform_ai_quota_tokens: parseInt(e.target.value, 10) || 0 }))} />
          </div>
          <div className="form-group" style={{marginBottom:8}}>
            <label style={{fontSize:12}}>备注（租户可见）</label>
            <input className="form-input" value={aiPolicyForm.byok_note}
              onChange={e => setAiPolicyForm(f => ({ ...f, byok_note: e.target.value }))} placeholder="例如：企业版客户已授权 BYOK" />
          </div>
          <button className="btn btn-outline btn-xs" disabled={aiPolicyBusy} onClick={saveAiPolicy}>{aiPolicyBusy ? '保存中…' : '保存 AI 权限'}</button>
        </div>}
        {detail.status !== 'purged' && detail.status !== 'deleted_pending' && <div className="detail-section">
          <div className="detail-section-title">MES 产品授权</div>
          {mesInfo && <div style={{fontSize:12,color:'var(--brand-text-muted)',marginBottom:8}}>
            套餐 {mesInfo.plan_code || '—'}
            {mesInfo.plan_allows ? ' · 允许 MES' : ' · 套餐不含 MES'}
            {mesInfo.settings?.provisioned ? ' · 已开通' : ' · 未开通'}
            {mesInfo.settings?.mes_tenant_code ? ' · 编码 ' + mesInfo.settings.mes_tenant_code : ''}
            {mesInfo.settings?.last_error ? <span style={{color:'#dc2626'}}> · 错误：{mesInfo.settings.last_error}</span> : null}
          </div>}
          <div className="form-group" style={{marginBottom:8}}>
            <label style={{fontSize:12}}>行业</label>
            <select className="form-input" value={mesIndustry} onChange={e => setMesIndustry(e.target.value)}>
              <option value="electronics">电子组装</option>
              <option value="injection">注塑</option>
              <option value="machining">机加</option>
            </select>
          </div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            <button className="btn btn-primary btn-xs" disabled={mesBusy} onClick={() => provisionMes(true)}>
              {mesBusy ? '处理中…' : '开通 MES'}
            </button>
            <button className="btn btn-outline btn-xs" disabled={mesBusy} onClick={() => provisionMes(false)}>
              停用 MES
            </button>
          </div>
        </div>}
        <div className="detail-section">
          <div className="detail-section-title">快速操作</div>
          <div className="detail-actions">
            {(detail.status === 'active' || detail.status === 'suspended' || detail.status === 'trial') &&
              <button className="btn btn-outline btn-xs" disabled={busy} onClick={toggleStatus}>
                {detail.status === 'active' ? '暂停租户' : '激活租户'}
              </button>}
            {(detail.status === 'active' || detail.status === 'suspended' || detail.status === 'trial') &&
              <button className="btn btn-outline btn-xs danger-text" disabled={busy} onClick={requestDelete}>
                申请注销（30天冷静期）
              </button>}
            {detail.status === 'deleted_pending' &&
              <button className="btn btn-outline btn-xs" disabled={busy} onClick={restoreTenant}>恢复租户</button>}
            {detail.status === 'deleted_pending' &&
              <button className="btn btn-danger btn-xs" disabled={busy} onClick={purgeTenant}>立即物理清除</button>}
            {detail.status !== 'purged' && detail.status !== 'deleted_pending' &&
              <button className="btn btn-outline btn-xs" disabled={busy} onClick={() => { setPlanId(detail.plan_id || ''); setShowPlan(true); }}>变更套餐</button>}
            {detail.status === 'trial' &&
              <button className="btn btn-outline btn-xs" disabled={busy} onClick={convertTrial}>试用转正式</button>}
            {detail.status !== 'purged' &&
              <button className="btn btn-outline btn-xs danger-text" disabled={busy} onClick={resetPassword}>重置管理员密码</button>}
          </div>
        </div>
        {detail.status !== 'purged' && detail.status !== 'deleted_pending' && <div className="detail-section">
          <div className="detail-section-title" style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span>Open API 密钥</span>
            <button className="btn btn-xs btn-outline" disabled={busy} onClick={() => { setCreatedKey(null); setShowKeyModal(true); }}><Icons.Plus/> 新建</button>
          </div>
          {apiKeys.length === 0
            ? <p style={{fontSize:12,color:'var(--brand-text-muted)'}}>暂无 API 密钥</p>
            : apiKeys.map(k => (
              <div key={k.id} className="detail-row" style={{flexDirection:'column',alignItems:'stretch',gap:4,padding:'8px 0'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span style={{fontWeight:600,fontSize:13}}>{k.name}</span>
                  <button className="btn btn-xs btn-danger" onClick={() => revokeApiKey(k)}>吊销</button>
                </div>
                <div style={{fontSize:11,color:'var(--brand-text-muted)'}}>{k.key_prefix} · {(k.scopes||[]).join(', ')}</div>
                <div style={{fontSize:11,color:'var(--brand-text-muted)'}}>
                  调用 {k.use_count||0} 次
                  {k.last_used_at ? ' · 最近 ' + new Date(k.last_used_at).toLocaleString('zh-CN') : ''}
                </div>
                {k.expires_at && <div style={{fontSize:11,color:'var(--brand-text-muted)'}}>到期 {new Date(k.expires_at).toLocaleDateString('zh-CN')}</div>}
              </div>
            ))}
        </div>}
      </div>
      {showKeyModal && <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowKeyModal(false)}>
        <div className="modal" style={{width:400}}>
          <div className="modal-header">新建 API 密钥</div>
          <div className="form-group">
            <label>用途名称</label>
            <input className="form-input" value={newKeyName} onChange={e => setNewKeyName(e.target.value)} placeholder="如 MES 对接" />
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline" onClick={() => setShowKeyModal(false)}>取消</button>
            <button className="btn btn-primary" disabled={busy} onClick={createApiKey}>创建</button>
          </div>
        </div>
      </div>}
      {createdKey && <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setCreatedKey(null)}>
        <div className="modal" style={{width:480}}>
          <div className="modal-header">密钥已创建（仅显示一次）</div>
          <p style={{fontSize:12,color:'#dc2626',marginBottom:12}}>{createdKey.warning}</p>
          <div style={{background:'#1a1a1a',color:'#e5e5e5',padding:12,borderRadius:8,fontSize:12,wordBreak:'break-all',fontFamily:'monospace'}}>{createdKey.key}</div>
          <div className="modal-footer">
            <button className="btn btn-primary" onClick={() => { navigator.clipboard?.writeText(createdKey.key); alert('已复制'); }}>复制</button>
            <button className="btn btn-outline" onClick={() => setCreatedKey(null)}>关闭</button>
          </div>
        </div>
      </div>}
      {showPlan && <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowPlan(false)}>
        <div className="modal" style={{width:400}}>
          <div className="modal-header">变更套餐</div>
          <div className="form-group">
            <label>选择套餐</label>
            <select className="form-input" value={planId} onChange={e => setPlanId(e.target.value)}>
              <option value="">请选择</option>
              {plans.map(p => <option key={p.id} value={p.id}>{p.name} ({p.code})</option>)}
            </select>
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline" onClick={() => setShowPlan(false)}>取消</button>
            <button className="btn btn-primary" disabled={busy || !planId} onClick={changePlan}>确认变更</button>
          </div>
        </div>
      </div>}
    </div>
  </>;
}

const STATUS_FILTERS = [
  { key: 'all', label: '全部' },
  { key: 'active', label: '已激活' },
  { key: 'trial', label: '试用中' },
  { key: 'suspended', label: '已暂停' },
  { key: 'deleted_pending', label: '待删除' },
];

export function Tenants() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [adminUser, setAdminUser] = useState('');
  const [adminPass, setAdminPass] = useState('');
  const [detail, setDetail] = useState(null);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch('/api/v1/platform/tenants').then(d => {
      setList(d.data || []);
      setLoading(false);
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { all: list.length, active: 0, trial: 0, suspended: 0, deleted_pending: 0, purged: 0 };
    list.forEach(t => { if (c[t.status] != null) c[t.status]++; });
    return c;
  }, [list]);

  const filtered = useMemo(() => {
    const kw = q.trim().toLowerCase();
    return list.filter(t => {
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      if (!kw) return true;
      return (t.code || '').toLowerCase().includes(kw)
        || (t.name || '').toLowerCase().includes(kw)
        || (t.plan_name || '').toLowerCase().includes(kw);
    });
  }, [list, q, statusFilter]);

  const create = async () => {
    if (!code.trim() || !name.trim()) return;
    setCreating(true);
    const body = { code: code.trim(), name: name.trim() };
    if (adminUser.trim()) body.admin_username = adminUser.trim();
    if (adminPass.trim()) body.admin_password = adminPass.trim();
    const d = await apiFetch('/api/v1/platform/tenants', { method: 'POST', body });
    setCreating(false);
    if (d.code === 0) {
      setShow(false); setCode(''); setName(''); setAdminUser(''); setAdminPass('');
      const admin = d.data?.admin;
      if (admin) alert('租户创建成功\n\n管理员账号: ' + admin.username + '\n初始密码: ' + admin.password + '\n\n请妥善保存，关闭后不可再次查看。');
      else if (d.data?.admin_error) alert('租户已创建，但管理员账号失败: ' + d.data.admin_error);
      load();
    } else alert(d.message);
  };

  const onDetailUpdated = (t) => {
    if (t.status === 'purged') {
      setList(prev => prev.filter(x => x.id !== t.id));
      return;
    }
    setList(prev => prev.map(x => x.id === t.id ? { ...x, ...t } : x));
  };

  return (
    <div className="pg">
      <div className="pg-hero">
        <div>
          <div className="pg-kicker">租户与商务</div>
          <h1 className="pg-title">租户管理</h1>
          <p className="pg-sub">开户、试用转正、套餐变更、AI / MES 授权与注销冷静期</p>
        </div>
        <div className="pg-hero-actions">
          <button className="btn btn-outline btn-xs" onClick={async () => {
            const d = await apiFetch('/api/v1/platform/tenants/purge-expired', { method: 'POST' });
            if (d.code === 0) { alert('已清除 ' + ((d.data && d.data.purged_count) || 0) + ' 个过期租户'); load(); }
            else alert(d.message || '操作失败');
          }}>清除过期租户</button>
          <button className="btn btn-primary" onClick={() => setShow(true)}><Icons.Plus />新建租户</button>
        </div>
      </div>

      <div className="pg-chips">
        {STATUS_FILTERS.map(f => (
          <button
            key={f.key}
            type="button"
            className={'pg-chip' + (statusFilter === f.key ? ' active' : '')}
            onClick={() => setStatusFilter(f.key)}
          >
            <span>{f.label}</span>
            <strong>{counts[f.key] ?? 0}</strong>
          </button>
        ))}
      </div>

      <div className="pg-toolbar">
        <input
          className="form-input pg-search"
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="搜索编码、名称或套餐…"
        />
        <span className="pg-toolbar-meta">共 {filtered.length} / {list.length} 户</span>
        <button className="btn btn-outline btn-xs" onClick={load} disabled={loading}>
          <Icons.Refresh w={12} h={12} /> 刷新
        </button>
      </div>

      <div className="card pg-table-card">
        {loading ? <div className="empty"><span className="spinner" /></div>
          : filtered.length === 0 ? (
            <div className="empty">
              <Icons.Empty />
              <div className="empty-text">{list.length === 0 ? '暂无租户' : '无匹配结果'}</div>
              {list.length === 0 && (
                <button className="btn btn-primary btn-xs" style={{ marginTop: 12 }} onClick={() => setShow(true)}>
                  <Icons.Plus /> 创建第一个租户
                </button>
              )}
            </div>
          ) : (
            <div className="table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th className="col-code">编码</th>
                    <th className="col-name">企业名称</th>
                    <th className="col-status">状态</th>
                    <th className="col-shrink">套餐</th>
                    <th className="col-num">用户上限</th>
                    <th className="col-num">工位上限</th>
                    <th className="col-date">创建时间</th>
                    <th className="col-actions"> </th>
                  </tr>
                </thead>
                <AdminPagedTbody rows={filtered.map(t => (
                    <tr
                      key={t.id}
                      className={detail?.id === t.id ? 'is-selected' : ''}
                      onClick={() => setDetail(t)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="col-code"><strong className="pg-code">{t.code}</strong></td>
                      <td className="col-name">
                        <div className="pg-name">{t.name}</div>
                        {t.trial_ends_at && t.status === 'trial' && (
                          <div className="cell-sub">试用至 {new Date(t.trial_ends_at).toLocaleDateString('zh-CN')}</div>
                        )}
                      </td>
                      <td className="col-status"><span className={`tag ${tagClass(t.status)}`}>{statusName(t.status)}</span></td>
                      <td>{t.plan_name || '—'}</td>
                      <td className="col-num">{t.max_users ?? '—'}</td>
                      <td className="col-num">{t.max_stations ?? '—'}</td>
                      <td className="col-date cell-muted">{new Date(t.created_at).toLocaleString('zh-CN')}</td>
                      <td className="col-actions" onClick={e => e.stopPropagation()}>
                        <button className="btn btn-outline btn-xs" onClick={() => setDetail(t)}>详情</button>
                      </td>
                    </tr>
                  ))} />
              </table>
            </div>
          )}
      </div>

      {show && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShow(false)}>
          <div className="modal">
            <div className="modal-header">创建新租户</div>
            <p className="modal-hint">创建后默认进入试用；管理员密码请妥善保存，关闭后不可再次查看。</p>
            <div className="form-group"><label>租户编码</label><input className="form-input" value={code} onChange={e => setCode(e.target.value)} placeholder="如: demo-001" /></div>
            <div className="form-group"><label>企业名称</label><input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="如: 演示电子厂" /></div>
            <div className="form-group"><label>管理员账号（可选）</label><input className="form-input" value={adminUser} onChange={e => setAdminUser(e.target.value)} placeholder="留空自动生成 编码-admin" /></div>
            <div className="form-group"><label>管理员密码（可选）</label><input className="form-input" type="password" value={adminPass} onChange={e => setAdminPass(e.target.value)} placeholder="留空随机生成" /></div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShow(false)}>取消</button>
              <button className="btn btn-primary" disabled={creating || !code.trim() || !name.trim()} onClick={create}>
                {creating ? '创建中…' : '创建'}
              </button>
            </div>
          </div>
        </div>
      )}

      {detail && (
        <TenantDetailPanel
          tenant={detail}
          onClose={() => setDetail(null)}
          onUpdated={onDetailUpdated}
        />
      )}
    </div>
  );
}
